import useLocale from 'next-translate/useTranslation';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';

import Turnstile, {
  isTurnstileEnabled,
  TURNSTILE_ERROR_MESSAGE,
  type TurnstileHandle,
} from '@/components/Turnstile';

import { CONTACT_FORM_TURNSTILE_ACTION } from '@/constants/turnstile';
import httpClient from '@/utils/httpClient';

import Button from './buttons/Button';

type Inputs = {
  name: string;
  email: string;
  message: string;
};

export default function ContactUs() {
  const { t } = useLocale('contact');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();
  const turnstileRef = useRef<TurnstileHandle>(null);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      const turnstileToken = await turnstileRef.current?.getToken();
      if (isTurnstileEnabled && !turnstileToken) {
        toast.error(TURNSTILE_ERROR_MESSAGE);
        return;
      }

      await httpClient.post(
        '/api/send-email',
        JSON.stringify({ ...data, turnstileToken }),
        { skipErrorToast: true },
      );
      toast.success(t('Email sent successfully'));
      reset();
    } catch (error: any) {
      toast.error(
        (error?.response?.data?.error as string | undefined) ||
          (error?.message as string) ||
          t('Failed to send message. Please try again.'),
      );
    } finally {
      // Tokens are single-use — refresh for the next attempt either way.
      turnstileRef.current?.reset();
      setIsLoading(false);
    }
  };

  return (
    <div
      id='contact-us'
      className='overflow-hidden bg-white px-4 py-16 dark:bg-slate-900 sm:px-6 lg:px-8 lg:py-24'
    >
      <div className='relative mx-auto max-w-xl'>
        <svg
          className='absolute left-full translate-x-1/2 transform'
          width={404}
          height={404}
          fill='none'
          viewBox='0 0 404 404'
          aria-hidden='true'
        >
          <defs>
            <pattern
              id='85737c0e-0916-41d7-917f-596dc7edfa27'
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits='userSpaceOnUse'
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className='text-gray-200 dark:text-slate-600'
                fill='currentColor'
              />
            </pattern>
          </defs>
          <rect
            width={404}
            height={404}
            fill='url(#85737c0e-0916-41d7-917f-596dc7edfa27)'
          />
        </svg>
        <svg
          className='absolute bottom-0 right-full -translate-x-1/2 transform'
          width={404}
          height={404}
          fill='none'
          viewBox='0 0 404 404'
          aria-hidden='true'
        >
          <defs>
            <pattern
              id='85737c0e-0916-41d7-917f-596dc7edfa27'
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits='userSpaceOnUse'
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className='text-gray-200 dark:text-slate-800'
                fill='currentColor'
              />
            </pattern>
          </defs>
          <rect
            width={404}
            height={404}
            fill='url(#85737c0e-0916-41d7-917f-596dc7edfa27)'
          />
        </svg>
        <div className='text-center'>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('Contact Us')}
          </h2>
          <p className='mt-4 text-lg leading-6 text-gray-500 dark:text-slate-400'>
            {t('Please use the form below to contact us. Thank you!')}
          </p>
        </div>
        <div className='mt-12'>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className='grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'
          >
            <div className='sm:col-span-2'>
              <label
                htmlFor='name'
                className='block text-sm font-medium text-gray-700 dark:text-slate-400'
              >
                {t('Name')}
              </label>
              <div className='mt-1'>
                <input
                  {...register('name', {
                    required: true,
                    maxLength: {
                      value: 100,
                      message: 'Name must be 100 characters or less',
                    },
                  })}
                  type='text'
                  name='name'
                  id='name'
                  autoComplete='organization'
                  required
                  className={clsxm(
                    errors.name ? 'border-red-500' : 'border-gray-300',
                    'block w-full rounded-md  px-4 py-3 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white',
                  )}
                />
                {errors.name && (
                  <span className='text-red-500'>
                    {errors.name.message || 'Name is required'}
                  </span>
                )}
              </div>
            </div>
            <div className='sm:col-span-2'>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-700 dark:text-slate-400'
              >
                {t('Email')}
              </label>
              <div className='mt-1'>
                <input
                  {...register('email', {
                    maxLength: 40,
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  id='email'
                  required
                  name='email'
                  type='email'
                  autoComplete='email'
                  className={clsxm(
                    errors.email ? 'border-red-500' : 'border-gray-300',
                    'block w-full rounded-md  px-4 py-3 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white',
                  )}
                />
                {errors.email && (
                  <span className='text-red-500'>Invalid email address</span>
                )}
              </div>
            </div>

            <div className='sm:col-span-2'>
              <label
                htmlFor='message'
                className='block text-sm font-medium text-gray-700 dark:text-slate-400'
              >
                {t('Message')}
              </label>
              <div className='mt-1'>
                <textarea
                  required
                  {...register('message', {
                    required: true,
                    maxLength: {
                      value: 5000,
                      message: 'Message must be 5000 characters or less',
                    },
                  })}
                  id='message'
                  name='message'
                  rows={4}
                  className={clsxm(
                    errors.message ? 'border-red-500' : 'border-gray-300',
                    'block w-full rounded-md  px-4 py-3 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white',
                  )}
                  defaultValue=''
                />
                {errors.message && (
                  <span className='text-red-500'>
                    {errors.message.message || 'Message is required'}
                  </span>
                )}
              </div>
            </div>
            <Turnstile
              ref={turnstileRef}
              action={CONTACT_FORM_TURNSTILE_ACTION}
              context='contact'
              className='sm:col-span-2'
            />
            <div className='flex justify-end sm:col-span-2'>
              <Button variant='outline' type='submit' isLoading={isLoading}>
                <span>{t('Send Message')}</span>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
